import { AxiosResponse } from "axios";
import { getAxiosInstance } from "./api-utils";
import Worksite, { WorksiteHit } from "@/model/worksite";
import { UUIDTypes } from "uuid";
import { Paged, PAGE_SIZE } from "@/model/paged";

const WORKSITES = "/worksites";

export async function searchForWorksites(keywords: string, page = 0, size = PAGE_SIZE): Promise<Paged<WorksiteHit>> {
    const response: AxiosResponse<Paged<WorksiteHit>> = await getAxiosInstance().get(WORKSITES, { params: { keywords, page, size } });
    return response.data;
}

export async function getWorksite(id: number): Promise<Worksite> {
    const response: AxiosResponse<Worksite> = await getAxiosInstance().get(`${WORKSITES}/${id}`);
    return response.data;
}

export async function saveWorksite(model: { object: Worksite, initialObject: Worksite }) {
    const worksite = model.object;
    const response = await getAxiosInstance().patch(WORKSITES, worksite);
    if (response.status === 200) {
        // Attach new files
        const files = model.object.attachedFiles.map(file => file.file).filter(file => !(!file));
        await Promise.all(files.map(file => uploadAttachmentToWorksite(model.object.id, file)));

        // Remove removed files
        const removedFiles = model.initialObject.attachedFiles.filter(file => !model.object.attachedFiles.some(f => f.uuid === file.uuid));
        await Promise.all(removedFiles.map(file => deleteAttachmentFromWorksite(model.object.id, file.uuid)));
    }
    return response.data;
}

export async function createWorksite(worksite: Worksite) {
    const response: AxiosResponse<Worksite> = await getAxiosInstance().put(WORKSITES, worksite);
    return response.data.id;
}

export async function uploadAttachmentToWorksite(worksiteId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("objectId", worksiteId.toString());

    await getAxiosInstance({ excludeContentType: true }).post(`${WORKSITES}/${worksiteId}/_uploadAttachment`, formData);
}

export async function deleteAttachmentFromWorksite(worksiteId: number, attachmentId: UUIDTypes) {
    await getAxiosInstance().delete(`${WORKSITES}/${worksiteId}/attachments/${attachmentId}`);
}

export async function downloadAttachmentFromWorksite(worksiteId: number, attachmentId: UUIDTypes) {
    const url = await previewAttachmentFromWorksite(worksiteId, attachmentId);

    // Open in new tab (browser decides to display or download)
    window.open(url, "_blank");

    // Optionally revoke the URL after some time to free memory
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function previewAttachmentFromWorksite(worksiteId: number, attachmentId: UUIDTypes) {
    const res = await getAxiosInstance().get(`${WORKSITES}/${worksiteId}/attachments/${attachmentId}`, {
        responseType: 'blob'
    });

    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);

    return url;
}
